import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { Box, Flex } from "../../../../styled-system/jsx";
import { Button } from "../../../components/core/basic/Button";
import { SearchInput } from "../../../components/fields/base/TextField";
import { commaSearchFilterFn } from "../../../components/table/filterFns";
import { Table } from "../../../components/table/Table";
import { createAppColumnHelper, useClientTable } from "../../../components/table/tableFeatures";
import type { PersonStats, PersonStatus } from "../model";
import { TEAM_COLOR, TRELLIS_COLOR } from "../theme";
import { useActivityLabels } from "../useActivityLabels";
import { Pill, Section, Sparkline, StatusBadge, useCount, useRelativeTime } from "./primitives";

const columnHelper = createAppColumnHelper<PersonStats>();

/** Most worrying first when sorted ascending, so a sort on status surfaces who needs a nudge. */
const STATUS_ORDER: Record<PersonStatus, number> = { never: 0, invited: 1, inactive: 2, slowing: 3, active: 4 };

export function PeopleTable({
  people,
  days,
  onOpenPerson,
}: {
  people: PersonStats[];
  days: number;
  onOpenPerson: (userId: number) => void;
}) {
  const { t } = useTranslation(["user_activity"]);
  const labels = useActivityLabels();
  const relativeTime = useRelativeTime();
  const count = useCount();
  const [search, setSearch] = useState("");

  const columns = useMemo(
    () => [
      columnHelper.accessor((stats) => stats.person.name, {
        id: "person",
        header: t(($) => $.user_activity["Person"]),
        size: 230,
        enableHiding: false,
        sortFn: "alphanumeric",
        cell: ({ row }) => {
          const { person } = row.original;
          return (
            <Box minWidth="0">
              <Flex alignItems="center" gap="small" flexWrap="wrap">
                <Button variant="link" onPress={() => onOpenPerson(person.user_id)}>
                  {person.name}
                </Button>
                {person.is_trellis && <Pill tone="brand">{t(($) => $.user_activity["Trellis"])}</Pill>}
                {!person.has_access && person.membership_status !== "invited" && (
                  <Pill>{t(($) => $.user_activity["No access"])}</Pill>
                )}
              </Flex>
              <span className={css({ display: "block", color: "text.secondary", fontSize: "small" })}>
                {person.email}
              </span>
            </Box>
          );
        },
      }),
      columnHelper.accessor((stats) => STATUS_ORDER[stats.status], {
        id: "status",
        header: t(($) => $.user_activity["Status"]),
        size: 150,
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      }),
      columnHelper.accessor((stats) => stats.lastSeen?.getTime(), {
        id: "lastSeen",
        header: t(($) => $.user_activity["Last seen"]),
        size: 120,
        sortUndefined: "last",
        cell: ({ row }) =>
          row.original.lastSeen ?
            <span title={row.original.lastSeen.toLocaleString()}>{relativeTime(row.original.lastSeen)}</span>
          : <span className={css({ color: "text.secondary" })}>{t(($) => $.user_activity["Never"])}</span>,
      }),
      columnHelper.accessor((stats) => stats.activeDays, {
        id: "activeDays",
        header: t(($) => $.user_activity["Days active"]),
        size: 110,
        meta: { justify: "end", headerJustify: "end" },
        cell: ({ getValue }) => t(($) => $.user_activity["{active} of {days}"], { active: getValue(), days }),
      }),
      columnHelper.accessor((stats) => stats.current.views, {
        id: "views",
        header: t(($) => $.user_activity["Pages viewed"]),
        size: 120,
        meta: { justify: "end", headerJustify: "end" },
        cell: ({ getValue }) => count.whole(getValue()),
      }),
      columnHelper.accessor((stats) => stats.current.changes, {
        id: "changes",
        header: t(($) => $.user_activity["Changes"]),
        size: 110,
        meta: { justify: "end", headerJustify: "end" },
        cell: ({ getValue }) => count.whole(getValue()),
      }),
      columnHelper.accessor((stats) => stats.current.reports, {
        id: "reports",
        header: t(($) => $.user_activity["Reports"]),
        size: 100,
        meta: { justify: "end", headerJustify: "end" },
        cell: ({ getValue }) => count.whole(getValue()),
      }),
      columnHelper.accessor((stats) => stats.daily, {
        id: "trend",
        header: t(($) => $.user_activity["Daily activity"]),
        size: 140,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.current.all > 0 ?
            <Sparkline
              values={row.original.daily}
              color={row.original.person.is_trellis ? TRELLIS_COLOR : TEAM_COLOR}
              label={t(($) => $.user_activity["Daily activity"])}
            />
          : <span className={css({ color: "text.secondary" })}>–</span>,
      }),
      columnHelper.accessor((stats) => (stats.topFeature ? labels.feature(stats.topFeature) : undefined), {
        id: "topFeature",
        header: t(($) => $.user_activity["Uses most"]),
        size: 170,
        sortFn: "alphanumeric",
        sortUndefined: "last",
        cell: ({ getValue }) => getValue() ?? <span className={css({ color: "text.secondary" })}>–</span>,
      }),
    ],
    [t, labels, relativeTime, count, days, onOpenPerson],
  );

  const globalFilterFn = useMemo(
    () => commaSearchFilterFn<PersonStats>((stats) => [stats.person.name, stats.person.email, stats.person.role ?? ""]),
    [],
  );

  const table = useClientTable({
    data: people,
    columns,
    globalFilterFn,
    getRowId: (stats) => String(stats.person.user_id),
    initialState: { sorting: [{ id: "lastSeen", desc: true }] },
    state: { globalFilter: search },
    onGlobalFilterChange: (updater) => setSearch(typeof updater === "function" ? updater(search) : updater),
  });

  return (
    <Section
      title={t(($) => $.user_activity["People"])}
      description={t(
        ($) =>
          $.user_activity[
            "Everyone with access to this organization, plus anyone else who worked in it. Select a name to see what they did."
          ],
      )}
      aside={
        <SearchInput
          aria-label={t(($) => $.user_activity["Search people"])}
          placeholder={t(($) => $.user_activity["Search people"])}
          css={{ flex: "0 1 280px" }}
          value={search}
          onChange={setSearch}
        />
      }
    >
      <Table table={table} />
    </Section>
  );
}
